import { Controller } from "stimulus"

export default class extends Controller {
  static PAGE_WIDTH = 816
  static PAGE_HEIGHT = 1056
  static PAGE_MARGIN = 72
  static CONTENT_HEIGHT = 912

  connect() {
    this.pageLines = []
    this.waitForEditor()
  }

  disconnect() {
    if (this.resizeObserver) this.resizeObserver.disconnect()
    if (this.formatObserver) this.formatObserver.disconnect()
    if (this._sectionObserver) this._sectionObserver.disconnect()
    this.removeLines()
  }

  waitForEditor() {
    const editable = this.element.querySelector(".ProseMirror") || this.element.querySelector(".editable.body")
    if (editable) {
      this.setupObserver()
      setTimeout(() => this.update(), 500)
    } else {
      this._retryCount = (this._retryCount || 0) + 1
      if (this._retryCount < 20) {
        setTimeout(() => this.waitForEditor(), 250)
      }
    }

    // For sectioned documents, also observe new sections loading in via Turbo Frames
    if (this.element.querySelector(".sections-list")) {
      this._sectionObserver = new MutationObserver(() => {
        clearTimeout(this._sectionUpdateTimer)
        this._sectionUpdateTimer = setTimeout(() => {
          this.setupObserver()
          this.update()
        }, 500)
      })
      this._sectionObserver.observe(this.element.querySelector(".sections-list"), {
        childList: true, subtree: true
      })
    }
  }

  get isDocumentFormat() {
    return this.element.classList.contains("editor-format-default")
  }

  setupObserver() {
    if (this.resizeObserver) this.resizeObserver.disconnect()

    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(this._t)
      this._t = setTimeout(() => this.update(), 500)
    })

    // Observe all section editors or the single editor
    const sectionEditors = this.element.querySelectorAll(".section-editor-instance")
    if (sectionEditors.length > 0) {
      sectionEditors.forEach(el => this.resizeObserver.observe(el))
    } else {
      const editable = this.element.querySelector(".ProseMirror") || this.element.querySelector(".editable.body")
      if (editable) this.resizeObserver.observe(editable)
    }

    if (!this.formatObserver) {
      this.formatObserver = new MutationObserver(() => {
        if (this.isDocumentFormat) {
          this.update()
        } else {
          this.removeLines()
        }
      })
      this.formatObserver.observe(this.element, { attributes: true, attributeFilter: ["class"] })
    }
  }

  update() {
    if (!this.isDocumentFormat) return

    // For sectioned documents, measure total height across all section editors
    const sectionEditors = this.element.querySelectorAll(".section-editor-instance")
    const isSectioned = sectionEditors.length > 0

    const editable = isSectioned ? null : (this.element.querySelector(".ProseMirror") || this.element.querySelector(".editable.body"))
    if (!isSectioned && !editable) return

    // Find the wrapper for positioning page lines
    const wrapper = isSectioned
      ? (this.element.querySelector(".post-form-wrapper") || this.element.querySelector(".sections-editor-container")?.closest(".post-form-wrapper") || this.element)
      : (editable.closest(".post-form-wrapper") || editable.parentElement)
    if (!wrapper) return

    if (getComputedStyle(wrapper).position === "static") {
      wrapper.style.position = "relative"
    }

    let contentHeight = 0
    let contentStartOffset = 0

    if (isSectioned) {
      // Sum heights of all section editors
      const firstEditor = sectionEditors[0]
      contentStartOffset = firstEditor.getBoundingClientRect().top - wrapper.getBoundingClientRect().top

      sectionEditors.forEach(editor => {
        contentHeight += editor.scrollHeight
      })
      // Add gaps between sections (section containers have margins/padding)
      const sectionContainers = this.element.querySelectorAll(".post-section")
      if (sectionContainers.length > 1) {
        const first = sectionContainers[0].getBoundingClientRect()
        const last = sectionContainers[sectionContainers.length - 1].getBoundingClientRect()
        const lastEditor = sectionEditors[sectionEditors.length - 1]
        const totalSpan = (last.top + last.height) - first.top
        contentHeight = totalSpan
      }
    } else {
      const style = getComputedStyle(editable)
      const paddingTop = parseInt(style.paddingTop) || 0
      const paddingBottom = parseInt(style.paddingBottom) || 0
      contentHeight = editable.scrollHeight - paddingTop - paddingBottom
      contentStartOffset = editable.offsetTop - wrapper.offsetTop + paddingTop
    }

    if (contentHeight <= 0) return

    const pageContent = this.constructor.CONTENT_HEIGHT
    const totalPages = Math.max(1, Math.ceil(contentHeight / pageContent))

    if (totalPages === this._lastPageCount) return
    this._lastPageCount = totalPages

    this.removeLines()

    for (let p = 1; p < totalPages; p++) {
      const y = contentStartOffset + (p * pageContent)

      const line = document.createElement("div")
      line.className = "document-page-line"
      line.style.cssText = `
        position: absolute;
        left: 0; right: 0;
        top: ${y}px;
        height: 0;
        border-top: 1px dashed #ccc;
        z-index: 10;
        pointer-events: none;
      `

      const label = document.createElement("span")
      label.style.cssText = `
        position: absolute;
        right: 4px; top: 4px;
        font-size: 10px;
        color: #aaa;
        font-family: -apple-system, BlinkMacSystemFont, sans-serif;
      `
      label.textContent = `Page ${p + 1}`
      line.appendChild(label)

      wrapper.appendChild(line)
      this.pageLines.push(line)
    }

    const existingCounter = wrapper.parentElement?.querySelector(".document-page-counter")
    if (existingCounter) existingCounter.remove()

    const counter = document.createElement("div")
    counter.className = "document-page-counter"
    counter.textContent = `${totalPages} ${totalPages === 1 ? "page" : "pages"}`
    if (wrapper.nextSibling) {
      wrapper.parentNode.insertBefore(counter, wrapper.nextSibling)
    } else if (wrapper.parentNode) {
      wrapper.parentNode.appendChild(counter)
    }
    this.pageLines.push(counter)
  }

  removeLines() {
    this.pageLines.forEach(el => el.remove())
    this.pageLines = []
    this._lastPageCount = 0
  }
}
